import { ExclamationCircleOutlined } from "@ant-design/icons";
import { Modal } from "components/styleguide";
import React, { useContext, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { UNSAFE_NavigationContext } from "react-router-dom";

type BlockableNavigator = {
  block?: (blocker: (transition: { retry: () => void }) => void) => () => void;
};

const useUnsavedChangesPrompt = (when: boolean, isSaving: boolean, content: string) => {
  const { t } = useTranslation();
  const { navigator } = useContext(UNSAFE_NavigationContext);

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!when || isSaving) {
        return;
      }

      event.preventDefault();
      event.returnValue = "";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isSaving, when]);

  useEffect(() => {
    const blockableNavigator = navigator as BlockableNavigator;

    if (!when || isSaving || !blockableNavigator.block) {
      return undefined;
    }

    const unblock = blockableNavigator.block((transition) => {
      Modal.confirm({
        title: t("confirmation"),
        content,
        icon: <ExclamationCircleOutlined />,
        okText: t("yes"),
        cancelText: t("no"),
        onOk: () => {
          unblock();
          setTimeout(() => transition.retry());
        },
      });
    });

    return unblock;
  }, [content, isSaving, navigator, t, when]);
};

export default useUnsavedChangesPrompt;
